import { cn } from '@amore/ui/cn'
import { Tooltip, TooltipContent, TooltipTrigger } from '@amore/ui/components/tooltip'
import { Check, Clock3, X } from 'lucide-react'
import type { Person } from '@/mock/types'
import { isPending, personName } from './model'

export type AttendanceState = 'confirmed' | 'pending' | 'declined'

export const attendanceStyles = {
  confirmed: { icon: Check, label: 'Přijde', className: 'bg-success text-success-foreground' },
  pending: {
    icon: Clock3,
    label: 'Čekáme na odpověď',
    className: 'bg-warning text-warning-foreground',
  },
  declined: { icon: X, label: 'Nepřijde', className: 'bg-danger text-danger-foreground' },
} satisfies Record<AttendanceState, { icon: typeof Check; label: string; className: string }>

export const stateOf = (person: Pick<Person, 'attendance'>): AttendanceState =>
  isPending(person as Person)
    ? 'pending'
    : person.attendance === 'confirmed'
      ? 'confirmed'
      : 'declined'

/**
 * One icon per person: green comes, yellow we're waiting, red won't come;
 * colour plus shape. The tooltip names the person. Used in the guest list and
 * for web replies, so a reply reads exactly like the list.
 */
export function AttendanceDots({
  people,
}: {
  people: { key: string; name: string; state: AttendanceState }[]
}) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      {people.map((person) => {
        const { icon: Icon, label, className } = attendanceStyles[person.state]
        const name = person.name.charAt(0).toUpperCase() + person.name.slice(1)
        return (
          <Tooltip key={person.key}>
            <TooltipTrigger asChild>
              <span
                // biome-ignore lint/a11y/noNoninteractiveTabindex: the tooltip names the person
                tabIndex={0}
                className={cn(
                  'grid size-6 place-items-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                  className,
                )}
              >
                <Icon aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
                <span className="sr-only">{`${name}: ${label}`}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {name} · {label.toLowerCase()}
            </TooltipContent>
          </Tooltip>
        )
      })}
    </span>
  )
}

export const partyDots = (persons: Person[]) =>
  persons.map((person) => ({ key: person.id, name: personName(person), state: stateOf(person) }))
